// src/pages/CRM/Core/components/ServiceReminders.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../../utils/axiosInstance';
import {
  ServiceReminderStatus,
  ServiceReminderStatusLabels,
  ServiceReminderStatusColors,
  ServiceReminderType,
  ServiceReminderTypeLabels,
  ServiceReminderTypeColors,
  RecurrenceType,
  RecurrenceTypeLabels,
} from '../crmEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const emptyForm = {
  title: '',
  description: '',
  service_type_id: '',
  service_type_name: '',
  service_date: '',
  reminder_type: 'OTHER',
  recurrence_type: 'ONE_TIME',
  recurrence_end_date: '',
  notify_via_sms: false,
  notify_via_email: true,
  sms_message: '',
  email_subject: '',
  email_body: '',
  remind_before_days: 1,
  remind_on_day: true,
};

export default function ServiceReminders({ contactId, chatbotId }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadReminders = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const resp = await axiosInstance.get(`/api/crm/service-reminders?contact_id=${contactId}&per_page=100`);
      if (resp.data.success) {
        setReminders(resp.data.data.reminders || []);
      }
    } catch (err) {
      console.error('Failed to load service reminders:', err);
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  const loadServiceTypes = useCallback(async () => {
    try {
      const resp = await axiosInstance.get('/api/crm/service-types');
      if (resp.data.success) {
        setServiceTypes(resp.data.data || []);
      }
    } catch (err) {
      console.error('Failed to load service types:', err);
    }
  }, []);

  useEffect(() => {
    loadReminders();
    loadServiceTypes();
  }, [loadReminders, loadServiceTypes]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (reminder) => {
    setEditingId(reminder.id);
    setForm({
      title: reminder.title || '',
      description: reminder.description || '',
      service_type_id: reminder.service_type_id || '',
      service_type_name: reminder.service_type_name || '',
      service_date: reminder.service_date ? reminder.service_date.slice(0, 16) : '',
      reminder_type: reminder.reminder_type || 'OTHER',
      recurrence_type: reminder.recurrence_type || 'ONE_TIME',
      recurrence_end_date: reminder.recurrence_end_date ? reminder.recurrence_end_date.slice(0, 16) : '',
      notify_via_sms: reminder.notify_via_sms || false,
      notify_via_email: reminder.notify_via_email || false,
      sms_message: reminder.sms_message || '',
      email_subject: reminder.email_subject || '',
      email_body: reminder.email_body || '',
      remind_before_days: reminder.remind_before_days ?? 1,
      remind_on_day: reminder.remind_on_day ?? true,
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.service_date) {
      setError('Title and service date are required');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        contact_id: contactId,
        chatbot_id: chatbotId || undefined,
        service_type_id: form.service_type_id || undefined,
      };

      if (editingId) {
        await axiosInstance.put(`/api/crm/service-reminders/${editingId}`, payload);
      } else {
        await axiosInstance.post('/api/crm/service-reminders', payload);
      }

      setDialogOpen(false);
      loadReminders();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (id) => {
    try {
      await axiosInstance.post(`/api/crm/service-reminders/${id}/complete`);
      loadReminders();
    } catch (err) {
      console.error('Failed to complete:', err);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await axiosInstance.delete(`/api/crm/service-reminders/${deletingId}`);
      setDeleteDialogOpen(false);
      setDeletingId(null);
      loadReminders();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleServiceTypeChange = (e) => {
    const typeId = e.target.value;
    const type = serviceTypes.find((t) => t.id === typeId);
    setForm((prev) => ({
      ...prev,
      service_type_id: typeId,
      service_type_name: type ? type.name : prev.service_type_name,
      title: type ? type.name : prev.title,
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600 }}>
          Service Reminders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          size="small"
          sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
        >
          New Reminder
        </Button>
      </Box>

      {reminders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ScheduleIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.12)', mb: 1 }} />
          <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
            No service reminders yet
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Title</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Reminder Type</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Service Type</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Service Date</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Frequency</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Channels</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }}>Status</TableCell>
                <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.75rem' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily, fontSize: '0.8rem' }}>{r.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={ServiceReminderTypeLabels[r.reminder_type] || 'Other'}
                      color={ServiceReminderTypeColors[r.reminder_type] || 'default'}
                      size="small"
                      sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>{r.service_type_name || '-'}</TableCell>
                  <TableCell sx={{ fontFamily, fontSize: '0.75rem' }}>{formatDate(r.service_date)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {r.recurrence_type && r.recurrence_type !== 'ONE_TIME' && (
                        <RepeatIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
                      )}
                      <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#64748b' }}>
                        {RecurrenceTypeLabels[r.recurrence_type] || 'One Time'}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {r.notify_via_email && (
                        <Tooltip title="Email"><EmailIcon sx={{ fontSize: 16, color: '#3b82f6' }} /></Tooltip>
                      )}
                      {r.notify_via_sms && (
                        <Tooltip title="SMS"><SmsIcon sx={{ fontSize: 16, color: '#10b981' }} /></Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ServiceReminderStatusLabels[r.status] || r.status}
                      color={ServiceReminderStatusColors[r.status] || 'default'}
                      size="small"
                      sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0} justifyContent="flex-end">
                      {r.status !== ServiceReminderStatus.COMPLETED && r.status !== ServiceReminderStatus.CANCELLED && (
                        <Tooltip title="Mark Complete">
                          <IconButton size="small" onClick={() => handleComplete(r.id)}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#059669' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(r)}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => { setDeletingId(r.id); setDeleteDialogOpen(true); }}>
                          <DeleteIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>
          {editingId ? 'Edit Service Reminder' : 'New Service Reminder'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily }}>Service Type</InputLabel>
              <Select
                value={form.service_type_id}
                onChange={handleServiceTypeChange}
                label="Service Type"
                sx={{ fontFamily }}
              >
                <MenuItem value="">
                  <em>Custom</em>
                </MenuItem>
                {serviceTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id} sx={{ fontFamily }}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {!form.service_type_id && (
              <TextField
                label="Custom Service Type"
                value={form.service_type_name}
                onChange={(e) => setForm((p) => ({ ...p, service_type_name: e.target.value }))}
                size="small"
                fullWidth
                InputProps={{ sx: { fontFamily } }}
                InputLabelProps={{ sx: { fontFamily } }}
              />
            )}

            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              size="small"
              fullWidth
              required
              InputProps={{ sx: { fontFamily } }}
              InputLabelProps={{ sx: { fontFamily } }}
            />

            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              size="small"
              fullWidth
              multiline
              rows={2}
              InputProps={{ sx: { fontFamily } }}
              InputLabelProps={{ sx: { fontFamily } }}
            />

            <TextField
              label="Service Date"
              type="datetime-local"
              value={form.service_date}
              onChange={(e) => setForm((p) => ({ ...p, service_date: e.target.value }))}
              size="small"
              fullWidth
              required
              InputLabelProps={{ shrink: true, sx: { fontFamily } }}
              InputProps={{ sx: { fontFamily } }}
            />

            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily }}>Reminder Type</InputLabel>
              <Select
                value={form.reminder_type}
                onChange={(e) => setForm((p) => ({ ...p, reminder_type: e.target.value }))}
                label="Reminder Type"
                sx={{ fontFamily }}
              >
                {Object.entries(ServiceReminderTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontFamily }}>Frequency</InputLabel>
              <Select
                value={form.recurrence_type}
                onChange={(e) => setForm((p) => ({ ...p, recurrence_type: e.target.value }))}
                label="Frequency"
                sx={{ fontFamily }}
              >
                {Object.entries(RecurrenceTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key} sx={{ fontFamily }}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.recurrence_type !== 'ONE_TIME' && (
              <TextField
                label="Recurrence End Date (optional)"
                type="datetime-local"
                value={form.recurrence_end_date}
                onChange={(e) => setForm((p) => ({ ...p, recurrence_end_date: e.target.value }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                InputProps={{ sx: { fontFamily } }}
                helperText="Leave empty for indefinite recurrence"
              />
            )}

            <Box>
              <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                Notification Channels
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.notify_via_email}
                    onChange={(e) => setForm((p) => ({ ...p, notify_via_email: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Email</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.notify_via_sms}
                    onChange={(e) => setForm((p) => ({ ...p, notify_via_sms: e.target.checked }))}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>SMS</Typography>}
              />
            </Box>

            {form.notify_via_email && (
              <>
                <TextField
                  label="Custom Email Subject (optional)"
                  value={form.email_subject}
                  onChange={(e) => setForm((p) => ({ ...p, email_subject: e.target.value }))}
                  size="small"
                  fullWidth
                  InputProps={{ sx: { fontFamily } }}
                  InputLabelProps={{ sx: { fontFamily } }}
                />
                <TextField
                  label="Custom Email Body (optional)"
                  value={form.email_body}
                  onChange={(e) => setForm((p) => ({ ...p, email_body: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  rows={3}
                  InputProps={{ sx: { fontFamily } }}
                  InputLabelProps={{ sx: { fontFamily } }}
                />
              </>
            )}

            {form.notify_via_sms && (
              <TextField
                label="Custom SMS Message (optional)"
                value={form.sms_message}
                onChange={(e) => setForm((p) => ({ ...p, sms_message: e.target.value }))}
                size="small"
                fullWidth
                multiline
                rows={2}
                InputProps={{ sx: { fontFamily } }}
                InputLabelProps={{ sx: { fontFamily } }}
              />
            )}

            <Box>
              <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, mb: 0.5 }}>
                Reminder Timing
              </Typography>
              {form.recurrence_type === 'DAILY' ? (
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b' }}>
                  Daily reminders are sent on each occurrence day automatically.
                </Typography>
              ) : (
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    label="Days before"
                    type="number"
                    value={form.remind_before_days}
                    onChange={(e) => {
                      let val = parseInt(e.target.value) || 0;
                      const max = form.recurrence_type === 'WEEKLY' ? 6 : form.recurrence_type === 'MONTHLY' ? 14 : 365;
                      if (val > max) val = max;
                      if (val < 0) val = 0;
                      setForm((p) => ({ ...p, remind_before_days: val }));
                    }}
                    size="small"
                    sx={{ width: 120 }}
                    InputProps={{
                      sx: { fontFamily },
                      inputProps: {
                        min: 0,
                        max: form.recurrence_type === 'WEEKLY' ? 6 : form.recurrence_type === 'MONTHLY' ? 14 : 365,
                      },
                    }}
                    InputLabelProps={{ sx: { fontFamily } }}
                    helperText={
                      form.recurrence_type === 'WEEKLY' ? 'Max 6 days' :
                      form.recurrence_type === 'MONTHLY' ? 'Max 14 days' : ''
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.remind_on_day}
                        onChange={(e) => setForm((p) => ({ ...p, remind_on_day: e.target.checked }))}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontFamily, fontSize: '0.8rem' }}>Also remind on the day</Typography>}
                  />
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontFamily }}>Delete Service Reminder</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>
            Are you sure you want to delete this service reminder?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ fontFamily, textTransform: 'none', borderRadius: '8px' }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
